<template>
  <div class="admin-section">
    <div class="section-header">
      <h2 class="section-title">All Users</h2>
      <Button
        @click="loadUsers"
        :disabled="isLoadingUsers"
        variant="default"
        size="sm"
        class="refresh-btn"
      >
        <span v-if="!isLoadingUsers">Refresh</span>
        <span v-else>Loading...</span>
      </Button>
    </div>

    <div v-if="usersError" class="message error">
      {{ usersError }}
    </div>

    <div v-if="isLoadingUsers && users.length === 0" class="loading-state">
      Loading users...
    </div>

    <div v-else-if="users.length === 0" class="empty-state">
      No users found.
    </div>

    <div v-else class="users-list">
      <div
        v-for="user in users"
        :key="user.id"
        class="user-item"
      >
        <div class="user-header">
          <div class="user-info" @click="toggleUser(user.id)">
            <div class="user-email">{{ user.email }}</div>
            <div class="user-meta">
              <span class="user-role" :class="user.roles">{{ user.roles }}</span>
              <span class="user-date">{{ formatDate(user.created_at) }}</span>
            </div>
          </div>
          <div class="user-actions">
            <div class="admin-toggle" @click.stop>
              <label class="toggle-label">
                <input
                  type="checkbox"
                  :checked="user.roles === 'admin'"
                  :disabled="isUpdatingRole[user.id] || user.id === currentUserId"
                  @change="toggleAdminRole(user)"
                  class="toggle-checkbox"
                />
                <span class="toggle-slider"></span>
                <span class="toggle-text">Admin</span>
              </label>
            </div>
            <Button
              v-if="user.id !== currentUserId"
              @click.stop="deleteUser(user)"
              :disabled="isDeletingUser[user.id] || !isAuthenticated"
              variant="destructive"
              size="sm"
              class="delete-user-btn"
              :title="`Delete user ${user.email}`"
            >
              <span v-if="!isDeletingUser[user.id]">Delete User</span>
              <span v-else>Deleting...</span>
            </Button>
            <span class="expand-icon" :class="{ expanded: expandedUsers[user.id] }" @click="toggleUser(user.id)">
              ▼
            </span>
          </div>
        </div>

        <div v-if="expandedUsers[user.id]" class="user-conversations">
          <details @toggle="toggleConversations(user.id, $event)">
            <summary class="conversations-header" style="outline: none; cursor: pointer;">
              <span class="expand-icon" :class="{ expanded: expandedConversations[user.id] }">
                ▼
              </span>
              <h3 class="conversations-title">Conversations</h3>
              <Button
                @click.stop="deleteUserConversations(user)"
                :disabled="isDeletingConversations[user.id] || !isAuthenticated"
                variant="destructive"
                size="sm"
                class="delete-conversations-btn"
              >
                <span v-if="!isDeletingConversations[user.id]">Delete All Conversations</span>
                <span v-else>Deleting...</span>
              </Button>
            </summary>
            <div v-if="deleteConversationMessage[user.id]" :class="['message', deleteConversationMessageType[user.id]]">
              {{ deleteConversationMessage[user.id] }}
            </div>
            <div v-if="loadingConversations[user.id]" class="loading-conversations">
              Loading conversations...
            </div>
            <div v-else-if="conversations[user.id]?.length === 0" class="no-conversations">
              No conversations found for this user.
            </div>
            <div v-else class="sessions-list">
              <div
                v-for="session in groupedSessions(user.id)"
                :key="session.sessionId"
                class="session-item"
              >
                <details class="session-details">
                  <summary class="session-header" style="outline: none; cursor: pointer;">
                    <span class="expand-icon session-expand-icon">▼</span>
                    <div class="session-meta">
                      <div class="session-id" :title="session.sessionId">
                        Session: {{ formatSessionId(session.sessionId) }}
                      </div>
                      <div class="session-summary">
                        {{ session.items.length }} message{{ session.items.length === 1 ? '' : 's' }}
                        · {{ formatDateTime(session.lastTimestamp) }}
                      </div>
                      <div
                        v-for="summary in summariesForSession(user.id, session.sessionId)"
                        :key="summary.id"
                        class="session-outcome"
                      >
                        <span v-if="summary.study_name" class="session-study">
                          Study: {{ summary.study_name }}
                        </span>
                        <span class="session-summary-id">
                          Summary #{{ summary.id }}<template v-if="!summary.study_id"> (general intake)</template>
                        </span>
                      </div>
                    </div>
                    <Button
                      @click.stop="deleteUserChatSession(user, session.sessionId)"
                      :disabled="isDeletingSession[`${user.id}-${session.sessionId}`] || !isAuthenticated"
                      variant="destructive"
                      size="sm"
                      class="delete-session-btn"
                    >
                      <span v-if="!isDeletingSession[`${user.id}-${session.sessionId}`]">Delete Session</span>
                      <span v-else>Deleting...</span>
                    </Button>
                  </summary>
                  <div class="conversations-list session-conversations">
                    <div
                      v-for="conversation in session.items"
                      :key="conversation.id"
                      class="conversation-item"
                    >
                      <div class="conversation-timestamp">
                        {{ formatDateTime(conversation.timestamp) }}
                      </div>
                      <div class="conversation-message user-message">
                        <strong>User:</strong> {{ conversation.message }}
                      </div>
                      <div class="conversation-message ai-message">
                        <strong>AI:</strong> {{ conversation.response }}
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </details>

          <details @toggle="toggleStudies(user.id, $event)" style="margin-top: 1rem;">
            <summary class="conversations-header" style="outline: none; cursor: pointer;">
              <span class="expand-icon" :class="{ expanded: expandedStudies[user.id] }">
                ▼
              </span>
              <h3 class="conversations-title">Assigned Studies</h3>
            </summary>
            <div v-if="loadingStudies[user.id]" class="loading-conversations">
              Loading studies...
            </div>
            <div v-else-if="userStudies[user.id]?.length === 0" class="no-conversations">
              No studies assigned to this user.
            </div>
            <div v-else class="studies-list">
              <div
                v-for="study in userStudies[user.id]"
                :key="study.id"
                class="study-item"
              >
                <div class="study-header-content">
                  <h4 class="study-name">{{ study.name }}</h4>
                  <Button
                    @click.stop="deleteStudyAssignment(user.id, study.id, study.name)"
                    :disabled="deletingStudyAssignment[`${user.id}-${study.id}`]"
                    variant="destructive"
                    size="sm"
                    class="delete-assignment-btn"
                  >
                    <span v-if="!deletingStudyAssignment[`${user.id}-${study.id}`]">Delete Assignment</span>
                    <span v-else>Deleting...</span>
                  </Button>
                </div>
                <!-- <p v-if="study.description" class="study-description">{{ study.description }}</p> -->
              </div>
            </div>
          </details>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { Button } from '@/components/ui/button'
import { API_ENDPOINTS, HTTP_STATUS, ROUTES, ERROR_MESSAGES } from '@/constants'

export default {
  name: 'UsersManagement',
  components: {
    Button
  },
  props: {
    currentUserId: {
      type: Number,
      default: null
    },
    isAuthenticated: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      users: [],
      isLoadingUsers: false,
      usersError: '',
      expandedUsers: {},
      expandedConversations: {},
      expandedStudies: {},
      conversations: {},
      loadingConversations: {},
      userStudies: {},
      loadingStudies: {},
      sessionSummaries: {},
      loadingSummaries: {},
      isUpdatingRole: {},
      roleUpdateError: '',
      isDeletingConversations: {},
      deleteConversationMessage: {},
      deleteConversationMessageType: {},
      isDeletingSession: {},
      deletingStudyAssignment: {},
      isDeletingUser: {}
    }
  },
  mounted() {
    this.loadUsers()
  },
  methods: {
    async loadUsers() {
      if (!this.isAuthenticated) {
        return
      }

      this.isLoadingUsers = true
      this.usersError = ''

      try {
        const response = await api.get(API_ENDPOINTS.ADMIN_USERS)
        this.users = response.data || []
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        this.usersError = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      } finally {
        this.isLoadingUsers = false
      }
    },
    async toggleUser(userId) {
      if (this.expandedUsers[userId]) {
        this.expandedUsers[userId] = false
      } else {
        this.expandedUsers[userId] = true
        await Promise.all([
          this.loadUserConversations(userId),
          this.loadUserStudies(userId),
          this.loadUserSummaries(userId)
        ])
      }
    },
    toggleConversations(userId, event) {
      this.expandedConversations[userId] = event.target.open
    },
    toggleStudies(userId, event) {
      this.expandedStudies[userId] = event.target.open
    },
    async loadUserStudies(userId) {
      if (this.userStudies[userId]) {
        return
      }

      this.loadingStudies[userId] = true

      try {
        const response = await api.get(API_ENDPOINTS.ADMIN_USER_STUDIES(userId))
        this.userStudies[userId] = response.data || []
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        this.userStudies[userId] = []
        console.error(`Error loading studies for user ${userId}:`, error)
      } finally {
        this.loadingStudies[userId] = false
      }
    },
    async loadUserSummaries(userId) {
      if (this.sessionSummaries[userId]) {
        return
      }

      this.loadingSummaries[userId] = true

      try {
        const response = await api.get(API_ENDPOINTS.ADMIN_PATIENT_SUMMARIES, {
          params: { user_id: userId }
        })
        const allSummaries = response.data || []
        // Map study_id -> session_id using modern summaries that carry both, so
        // legacy summaries (missing session_id) can fall back to study matching.
        const studyToSession = {}
        for (const summary of allSummaries) {
          if (summary.session_id && summary.study_id != null) {
            studyToSession[summary.study_id] = summary.session_id
          }
        }
        // Group summaries by their originating chat session id.
        const bySession = {}
        const pushToSession = (key, summary) => {
          if (!bySession[key]) {
            bySession[key] = []
          }
          bySession[key].push(summary)
        }
        for (const summary of allSummaries) {
          if (summary.session_id) {
            pushToSession(summary.session_id, summary)
          } else if (summary.study_id != null && studyToSession[summary.study_id]) {
            // Legacy row: attach to the session resolved from its study.
            pushToSession(studyToSession[summary.study_id], summary)
          } else {
            pushToSession('unknown', summary)
          }
        }
        this.sessionSummaries[userId] = bySession
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        this.sessionSummaries[userId] = {}
        console.error(`Error loading patient summaries for user ${userId}:`, error)
      } finally {
        this.loadingSummaries[userId] = false
      }
    },
    summariesForSession(userId, sessionId) {
      const bySession = this.sessionSummaries[userId]
      if (!bySession) return []
      return bySession[sessionId] || []
    },
    async loadUserConversations(userId) {
      if (this.conversations[userId]) {
        return
      }

      this.loadingConversations[userId] = true

      try {
        const response = await api.get(API_ENDPOINTS.ADMIN_USER_CONVERSATIONS(userId))
        this.conversations[userId] = response.data || []
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        this.conversations[userId] = []
        console.error(`Error loading conversations for user ${userId}:`, error)
      } finally {
        this.loadingConversations[userId] = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatSessionId(sessionId) {
      if (!sessionId) return '(none)'
      const id = String(sessionId)
      return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id
    },
    groupedSessions(userId) {
      const list = this.conversations[userId] || []
      const groups = new Map()
      for (const conv of list) {
        const key = conv.session_id || 'unknown'
        if (!groups.has(key)) {
          groups.set(key, { sessionId: key, items: [], lastTimestamp: conv.timestamp })
        }
        const group = groups.get(key)
        group.items.push(conv)
        if (new Date(conv.timestamp) > new Date(group.lastTimestamp)) {
          group.lastTimestamp = conv.timestamp
        }
      }
      // Sort sessions by most recent activity desc; messages within a session by time asc (oldest first)
      const sessions = Array.from(groups.values())
      for (const session of sessions) {
        session.items.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      }
      sessions.sort((a, b) => new Date(b.lastTimestamp) - new Date(a.lastTimestamp))
      return sessions
    },
    async toggleAdminRole(user) {
      if (!this.isAuthenticated) {
        return
      }

      if (user.id === this.currentUserId) {
        alert('You cannot remove your own admin status')
        this.$forceUpdate()
        return
      }

      const newRole = user.roles === 'admin' ? 'user' : 'admin'
      const confirmMessage = newRole === 'admin'
        ? `Grant admin status to ${user.email}?`
        : `Remove admin status from ${user.email}?`

      if (!confirm(confirmMessage)) {
        this.$forceUpdate()
        return
      }

      this.isUpdatingRole[user.id] = true
      this.roleUpdateError = ''

      try {
        const response = await api.patch(
          API_ENDPOINTS.ADMIN_UPDATE_USER_ROLE(user.id),
          { roles: newRole }
        )

        const userIndex = this.users.findIndex(u => u.id === user.id)
        if (userIndex !== -1) {
          this.users[userIndex] = response.data
        }
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        this.roleUpdateError = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
        alert(this.roleUpdateError)
        this.$forceUpdate()
      } finally {
        this.isUpdatingRole[user.id] = false
      }
    },
    async deleteUserConversations(user) {
      if (!this.isAuthenticated) {
        this.$router.push(ROUTES.HOME)
        return
      }

      if (!confirm(`Are you sure you want to delete all conversation history for ${user.email}? This action cannot be undone.`)) {
        return
      }

      this.isDeletingConversations[user.id] = true
      this.deleteConversationMessage[user.id] = ''
      this.deleteConversationMessageType[user.id] = ''

      try {
        const response = await api.delete(API_ENDPOINTS.ADMIN_DELETE_USER_CONVERSATIONS(user.id))

        this.deleteConversationMessage[user.id] = response.data.message || 'Conversation history deleted successfully'
        this.deleteConversationMessageType[user.id] = 'success'

        this.conversations[user.id] = []

        setTimeout(() => {
          this.deleteConversationMessage[user.id] = ''
        }, 5000)
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }

        this.deleteConversationMessage[user.id] = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
        this.deleteConversationMessageType[user.id] = 'error'
      } finally {
        this.isDeletingConversations[user.id] = false
      }
    },
    async deleteUserChatSession(user, sessionId) {
      if (!this.isAuthenticated) {
        this.$router.push(ROUTES.HOME)
        return
      }

      if (!confirm(`Delete this chat session for ${user.email}? This action cannot be undone.`)) {
        return
      }

      const key = `${user.id}-${sessionId}`
      this.isDeletingSession[key] = true

      try {
        await api.delete(API_ENDPOINTS.ADMIN_DELETE_USER_CHAT_SESSION(user.id, sessionId))

        // Remove conversations for this session from the local cache
        if (this.conversations[user.id]) {
          this.conversations[user.id] = this.conversations[user.id].filter(
            c => (c.session_id || 'unknown') !== sessionId
          )
        }
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }

        alert(error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR)
      } finally {
        this.isDeletingSession[key] = false
      }
    },
    async deleteStudyAssignment(userId, studyId, studyName) {
      if (!this.isAuthenticated) {
        this.$router.push(ROUTES.HOME)
        return
      }

      if (!confirm(`Are you sure you want to remove this user's assignment from study "${studyName}"? This will delete their patient summary data for this study.`)) {
        return
      }

      const key = `${userId}-${studyId}`
      this.deletingStudyAssignment[key] = true

      try {
        await api.delete(API_ENDPOINTS.ADMIN_DELETE_USER_STUDY(userId, studyId))

        // Remove the study from the local list
        if (this.userStudies[userId]) {
          this.userStudies[userId] = this.userStudies[userId].filter(s => s.id !== studyId)
        }
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }

        alert(error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR)
      } finally {
        this.deletingStudyAssignment[key] = false
      }
    },
    async deleteUser(user) {
      if (!this.isAuthenticated) {
        this.$router.push(ROUTES.HOME)
        return
      }

      if (user.id === this.currentUserId) {
        alert('You cannot delete your own account.')
        return
      }

      // Type-the-email confirmation given the destructive, irreversible scope.
      const warning =
        `You are about to PERMANENTLY delete user "${user.email}".\n\n` +
        `This will:\n` +
        `  • delete all of their conversations\n` +
        `  • remove their study assignments\n` +
        `  • PRESERVE their patient summaries (de-linked from the user)\n\n` +
        `This action cannot be undone.\n\n` +
        `Type the user's email to confirm:`
      const typed = window.prompt(warning, '')
      if (typed === null) {
        return
      }
      if (typed.trim().toLowerCase() !== user.email.trim().toLowerCase()) {
        alert('Email did not match. User was NOT deleted.')
        return
      }

      this.isDeletingUser[user.id] = true

      try {
        const response = await api.delete(API_ENDPOINTS.ADMIN_DELETE_USER(user.id))

        // Remove from local state
        this.users = this.users.filter(u => u.id !== user.id)
        delete this.expandedUsers[user.id]
        delete this.expandedConversations[user.id]
        delete this.expandedStudies[user.id]
        delete this.conversations[user.id]
        delete this.userStudies[user.id]
        delete this.sessionSummaries[user.id]
        delete this.isUpdatingRole[user.id]
        delete this.isDeletingConversations[user.id]
        delete this.deleteConversationMessage[user.id]
        delete this.deleteConversationMessageType[user.id]

        const message = response.data?.message || `User ${user.email} deleted successfully`
        const detached = response.data?.summaries_detached
        const convs = response.data?.conversations_deleted
        const detail = (typeof convs === 'number' && typeof detached === 'number')
          ? ` (${convs} conversation(s) deleted, ${detached} patient summary record(s) preserved)`
          : ''
        alert(message + detail)
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        alert(error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR)
      } finally {
        this.isDeletingUser[user.id] = false
      }
    }
  }
}
</script>

<style scoped>
.admin-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.refresh-btn {
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.message {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
}

.message.success {
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid #10b981;
}

.message.error {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #ef4444;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

.users-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.user-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #ffffff;
}

.user-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-header:hover {
  background-color: #f8fafc;
}

.user-info {
  flex: 1;
}

.user-email {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.user-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: 0.875rem;
  color: #64748b;
}

.user-role {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.75rem;
}

.user-role.user {
  background-color: #e0e7ff;
  color: #4338ca;
}

.user-role.admin {
  background-color: #fef3c7;
  color: #92400e;
}

.user-date {
  color: #94a3b8;
}

.user-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.admin-toggle {
  display: flex;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: relative;
  width: 44px;
  height: 24px;
  background-color: #cbd5e1;
  border-radius: 24px;
  transition: background-color 0.3s ease;
  cursor: pointer;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #ffffff;
  top: 3px;
  left: 3px;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-checkbox:checked + .toggle-slider {
  background-color: #3b82f6;
}

.toggle-checkbox:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-checkbox:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
}

.toggle-checkbox:checked ~ .toggle-text {
  color: #3b82f6;
}

.expand-icon {
  font-size: 0.75rem;
  color: #64748b;
  transition: transform 0.2s ease;
  display: inline-block;
  cursor: pointer;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.user-conversations {
  border-top: 1px solid #e2e8f0;
  padding: 1rem;
  background-color: #f8fafc;
}

.conversations-header {
  display: flex;
  /* justify-content: space-between; */
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.conversations-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-left: 30px;
}

.delete-conversations-btn {
  background-color: #dc2626;
  color: #ffffff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 315px;
}

.delete-conversations-btn:hover:not(:disabled) {
  background-color: #b91c1c;
}

.delete-conversations-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-conversations,
.no-conversations {
  padding: 1rem;
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
}

.conversations-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.session-item {
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px dashed #e2e8f0;
  margin-bottom: 0.75rem;
}

.session-details > summary {
  list-style: none;
}

.session-details > summary::-webkit-details-marker {
  display: none;
}

.session-details:not([open]) > summary {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.session-expand-icon {
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.session-details[open] > summary .session-expand-icon {
  transform: rotate(180deg);
}

.session-details > summary .session-meta {
  flex: 1;
  min-width: 0;
}

.session-meta {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.session-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e293b;
}

.session-summary {
  font-size: 0.75rem;
  color: #64748b;
}

.session-outcome {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.25rem;
}

.session-study,
.session-summary-id {
  display: inline-flex;
  align-items: center;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  line-height: 1.4;
}

.session-study {
  background-color: #dcfce7;
  color: #15803d;
}

.session-summary-id {
  background-color: #e0e7ff;
  color: #4338ca;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.delete-session-btn {
  background-color: #dc2626;
  color: #ffffff;
  border: none;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-session-btn:hover:not(:disabled) {
  background-color: #b91c1c;
}

.delete-session-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.session-conversations {
  margin-left: 1.5rem;
  padding-left: 0.75rem;
  border-left: 2px solid #e2e8f0;
}

.conversation-item {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
}

.conversation-timestamp {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-bottom: 0.75rem;
}

.conversation-message {
  margin-bottom: 0.75rem;
  line-height: 1.6;
  font-size: 0.9375rem;
}

.conversation-message:last-child {
  margin-bottom: 0;
}

.conversation-message strong {
  color: #1e293b;
  font-weight: 600;
}

.user-message {
  color: #334155;
}

.ai-message {
  color: #475569;
  padding-left: 1rem;
  border-left: 2px solid #3b82f6;
}

.studies-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}

.study-item {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
}

.study-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.study-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.delete-assignment-btn {
  background-color: #dc2626;
  color: #ffffff;
  border: none;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-assignment-btn:hover:not(:disabled) {
  background-color: #b91c1c;
}

.delete-assignment-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.study-status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-active {
  background-color: #dcfce7;
  color: #15803d;
}

.status-inactive {
  background-color: #f3f4f6;
  color: #6b7280;
}

.study-description {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

@media (max-width: 768px) {
  .user-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .user-meta {
    flex-wrap: wrap;
  }
}
</style>
